import React from "react";
import {useFormik} from "formik";
import {useFetch} from "hooks/useFetch";
import {useStore} from "hooks/useStore";
import * as yup from "yup";
import {Form, Modal, Alert, Input, Select, message} from "antd";
import {useTranslation} from "react-i18next";
import {projectInfoOptions, managerCompanyOptions} from "constants/constants";

const SUPPORTED_FIELDS = {
    mhNumber: {
        label: "MH Numarası",
        defaultValue: "",
        type: "string",
        required: true
    },
    ossNumber: {
        label: "ÖSS Poliçe Numarası",
        defaultValue: "",
        type: "number",
        required: true
    },
    projectInfo: {
        label: "Proje Üst Bilgi",
        defaultValue: undefined,
        type: "select",
        options: projectInfoOptions,
        required: true
    },
    managerCompany: {
        label: "Yönetici Firma",
        defaultValue: undefined,
        type: "select",
        options: managerCompanyOptions,
        required: true
    }
};

export const MissingCourierFieldsModal = ({
    courierId,
    mustBeFilledFields = [],
    companyName = "",
    visibility,
    onClose = async () => {},
    onFieldsFilled = async () => {}
}) => {
    const stores = useStore();
    const {t} = useTranslation();

    const [loading, courierData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.courierStore.getCourier.bind(null, {
            courierId
        }),
        condition: visibility && courierId,
        initialCall: false,
        dependencies: [visibility, courierId]
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            ...mustBeFilledFields
                ?.filter(field => SUPPORTED_FIELDS?.[field])
                ?.reduce((acc, field) => {
                    const payload = courierData?.data;
                    const raw = payload?.courier?.[field] ?? payload?.[field];
                    const fallback = SUPPORTED_FIELDS?.[field]?.defaultValue;
                    acc[field] = raw !== undefined && raw !== null && raw !== "" ? raw : fallback;
                    return acc;
                }, {})
        },
        validationSchema: yup.object().shape({
            ...mustBeFilledFields
                ?.filter(field => SUPPORTED_FIELDS?.[field])
                ?.reduce((acc, field) => {
                    const fieldConfig = SUPPORTED_FIELDS?.[field];
                    if (!fieldConfig) {
                        return (acc[field] = yup
                            .string()
                            .nullable()
                            .required(t("validation.required", {ns: "common"})));
                    }

                    if (fieldConfig && fieldConfig.type === "string") {
                        acc[field] = yup
                            .string()
                            .nullable()
                            .required(t("validation.required", {ns: "common"}));
                    }
                    if (fieldConfig && fieldConfig.type === "number") {
                        acc[field] = yup
                            .number()
                            .nullable()
                            .required(t("validation.required", {ns: "common"}));
                    }
                    if (fieldConfig && fieldConfig.type === "select") {
                        const optionValues = (fieldConfig.options || []).map(o => o.value);
                        acc[field] = yup
                            .string()
                            .required(t("validation.required", {ns: "common"}))
                            .oneOf(optionValues, t("validation.required", {ns: "common"}));
                    }
                    return acc;
                }, {})
        }),
        onSubmit: async values => {
            await handleSaveCourier({values});
        }
    });

    const handleSaveCourier = async ({values}) => {
        try {
            const payload = courierData?.data;
            const courier = payload?.courier ?? payload ?? {};
            const merged = {...courier, ...values};
            if (Object.prototype.hasOwnProperty.call(values, "projectInfo")) {
                merged.projectInfo = values.projectInfo || null;
            }
            if (Object.prototype.hasOwnProperty.call(values, "managerCompany")) {
                merged.managerCompany = values.managerCompany || null;
            }
            const success = await stores.courierStore.updateCourier({
                courier: merged
            });
            if (success) {
                onFieldsFilled(merged);
                onClose();
                return;
            } else {
                throw new Error("Kurye bilgileri güncellenemedi");
            }
        } catch (error) {
            message.error(error.message || "Kurye bilgileri güncellenemedi");
        }
    };

    return (
        <Form>
            <Modal
                visible={visibility}
                onCancel={onClose}
                onOk={formik.handleSubmit}
                okText={t("modal.saveButton", {ns: "common"})}
                cancelText={t("modal.cancelButton2", {ns: "common"})}
                confirmLoading={formik.isSubmitting}
                okButtonProps={{
                    disabled: formik.isSubmitting || loading
                }}
                cancelButtonProps={{
                    disabled: formik.isSubmitting || loading
                }}
                bodyStyle={{
                    display: "flex",
                    flexDirection: "column",
                    paddingTop: "40px",
                    gap: "8px 8px"
                }}
            >
                <Alert
                    message={<b>Eksik Kurye Bilgileri</b>}
                    description={
                        <>
                            {companyName ? (
                                <>
                                    <b>{companyName}</b> firmasına bağlı kuryeler için{" "}
                                </>
                            ) : null}
                            {mustBeFilledFields
                                ?.filter(field => SUPPORTED_FIELDS?.[field])
                                .map((field, index, array) => (
                                    <b>
                                        {" "}
                                        {SUPPORTED_FIELDS?.[field]?.label}
                                        {index < array.length - 1 ? ", " : ""}{" "}
                                    </b>
                                ))}{" "}
                            zorunludur.
                        </>
                    }
                    type="warning"
                />
                {mustBeFilledFields
                    ?.filter(field => SUPPORTED_FIELDS?.[field])
                    ?.map(field => (
                        <Form.Item
                            key={field}
                            label={SUPPORTED_FIELDS?.[field]?.label}
                            name={field}
                            validateStatus={
                                formik.errors[field] && formik.touched[field] ? "error" : "success"
                            }
                            help={
                                formik.errors[field] && formik.touched[field]
                                    ? formik.errors[field]
                                    : null
                            }
                            disabled={formik.isSubmitting || loading}
                            style={{
                                marginBottom: 0
                            }}
                        >
                            {SUPPORTED_FIELDS?.[field]?.type === "string" ? (
                                <Input
                                    name={field}
                                    value={formik?.values?.[field] || ""}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={formik.isSubmitting || loading}
                                />
                            ) : null}
                            {SUPPORTED_FIELDS?.[field]?.type === "number" ? (
                                <Input
                                    name={field}
                                    type="number"
                                    value={formik?.values?.[field] || ""}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={formik.isSubmitting || loading}
                                />
                            ) : null}
                            {SUPPORTED_FIELDS?.[field]?.type === "select" ? (
                                <Select
                                    placeholder="Seçiniz"
                                    style={{width: "100%"}}
                                    value={formik?.values?.[field]}
                                    onChange={v => formik.setFieldValue(field, v)}
                                    onBlur={() => formik.setFieldTouched(field, true)}
                                    disabled={formik.isSubmitting || loading}
                                >
                                    {(SUPPORTED_FIELDS?.[field]?.options || []).map(option => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            ) : null}
                        </Form.Item>
                    ))}
            </Modal>
        </Form>
    );
};
